import React, { useMemo } from 'react';
import { Item, Order } from 'store/cart/types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { getPrice } from 'utils/getPrice';
import { getTaxSummary } from 'utils/getTaxSummary';
import { ItemQuantityBlock } from '@hqo/react-components-library/dist/molecules/itemQuantityBlock';
import { usePriceQuery } from 'data/queries/getPriceQuery.hook';
import { selectResource } from 'store/resource/selectors';
import styled from 'styled-components';

const StyledItemQuantityBlock = styled(ItemQuantityBlock)`
  max-width: 100%;
  overflow: hidden;
  align-items: flex-start;
  gap: 15px;

  [class*='subtitle'],
  [class*='Subtitle'] {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
`;

interface ItemBlockProps {
  item: Item;
  locale: string;
  cart: Order;
  isFreeCart: boolean;
}

export const ItemBlock = ({ item, locale, cart, isFreeCart }: ItemBlockProps) => {
  const intl = useIntl();
  const freeText = intl.formatMessage({ id: 'free' });
  const { display_info } = item || {};
  const resource = useSelector(selectResource);

  const priceQuery = usePriceQuery({
    resourceId: resource?.id,
    timezone: resource?.timezone,
  });

  const creditTotal = priceQuery?.data?.price?.credit_total;
  const creditPrice = creditTotal ? Math.ceil(creditTotal) : undefined;

  const addOnsText = useMemo<string>(() => {
    const addOns = cart.items[0].resource_booking.add_ons;
    let text = '';

    addOns?.forEach(addOn => {
      text += `+${addOn.quantity} ${addOn.name}${addOn.id !== addOns[addOns.length - 1].id ? ', ' : ''}`;
    });

    return text;
  }, [cart]);

  const additionalPriceText = useMemo(() => {
    if (!cart?.total_summary?.tax_summary?.taxes_total || isFreeCart) {
      return null;
    }

    return `${getTaxSummary(cart, locale)} ${intl.formatMessage({ id: 'tax_text' })}`;
  }, [cart, intl, isFreeCart, locale]);

  return (
    <StyledItemQuantityBlock
      title={display_info.title}
      subtitle={addOnsText || display_info.subtitle}
      price={getPrice(isFreeCart, cart, freeText, locale, intl, creditPrice)}
      additionalPrice={additionalPriceText}
    />
  );
};
